import { AppointmentConfiguration, AppointmentPractitioner } from '@dreryk/egabinet-appointment-frontend-api';
import { PractitionerDetails } from '@dreryk/egabinet-personnel-frontend-api';

import { useTimetableState } from '@dreryk/react-calendar';
import { useCalendarRangeSelectorLegacy } from '@dreryk/react-components';

import { protectFeature } from '~libs.shared.system';
import {
   AppointmentsTimetable,
   PractitionerSelector,
   useAppointmentSchedules,
} from '~libs.shared.appointments-timetable';

import { AppointmentBookModal } from '../../appointment/create/AppointmentBookModal';
import { AppointmentPatientBookModal } from '../../appointment/create/AppointmentPatientBookModal';
import { AppointmentPatientBookModalLoader } from '../../appointment/create/AppointmentPatientBookModal.loader';
import { AppointmentRestoreModal } from '../../appointment/update/AppointmentRestoreModal';
import { AppointmentTermUpdateModal } from '../../appointment/update/AppointmentTermUpdateModal';
import { CalendarSelectedDate, CalendarSelectedDateSetter } from '../../CalendarController.types';
import { CalendarSidePanel } from '../../SidePanel/CalendarSidePanel';
import { MiniCalendar } from '../../SidePanel/MiniCalendar';
import { AppointmentContextDialog } from '../shared/contextDialog/AppointmentContextDialog';
import { getMinMaxTime } from '../shared/utils/getMinMaxTime';
import { TimetablePlaceholder } from '../shared/TimetablePlaceholder/TimetablePlaceholder';
import {
   useCancelledVisibilityPreferenceState,
   useScheduleOnlyVisibilityPreferenceState,
} from '../shared/preferences/preferences.storage';
import { WaitlistEntrySection } from '../shared/WaitlistEntrySection/WaitlistEntrySection';
import { WaitlistEntrySectionLoader } from '../shared/WaitlistEntrySection/WaitlistEntrySection.loader';
import { AppointmentWaitlistPatientBookModal } from '../../appointment/create/waitlist/AppointmentWaitlistPatientBookModal';

import { CalendarPatientSection } from './CalendarPatientSection';
import { CalendarPatientSectionLoader } from './CalendarPatientSection.loader';
import { MiniCalendarNextAvailableTimeSlot } from './MiniCalendarNextAvailableTimeSlot';
import {
   useNextAvailableTimeSlotTools,
   usePractitionerInitialSelection,
   useWeekendsVisibilityPreference,
} from './SpecialistTimetable.hooks';
import { SpecialistTimetableActions } from './actions/SpecialistTimetableActions';

type SpecialistTimetableProps = {
   readonly bookPatientId: number | undefined;
   readonly updateTermAppointmentId: number | undefined;
   readonly restoreAppointmentId: number | undefined;
   readonly waitlistEntryId: number | undefined;
   readonly practitionerId: number | undefined;
   readonly selectedDate: CalendarSelectedDate;
   readonly setSelectedDate: CalendarSelectedDateSetter;
   readonly currentPractitioner: PractitionerDetails;
   readonly practitioners: AppointmentPractitioner[];
   readonly appointmentConfiguration: AppointmentConfiguration;
};

// eslint-disable-next-line max-lines-per-function, max-statements
export const SpecialistTimetable = (props: SpecialistTimetableProps): JSX.Element => {
   const [preferCancelledVisitsVisible, setCancelledVisibilityPreference] = useCancelledVisibilityPreferenceState();
   const [preferScheduleOnlyVisible] = useScheduleOnlyVisibilityPreferenceState();
   const [preferWeekendsVisible] = useWeekendsVisibilityPreference();

   const { selectedPractitioner, setSelectedPractitioner } = PractitionerSelector.useState(
      props.practitionerId ?? props.currentPractitioner.id,
      props.practitioners
   );
   usePractitionerInitialSelection(props.practitionerId, props.practitioners, setSelectedPractitioner);

   const { dateRange } = useTimetableState(props.selectedDate, Boolean(preferWeekendsVisible));

   const { appointmentSchedules, appointmentSchedulesFetching, refetchAppointmentSchedules } = useAppointmentSchedules({
      dateRange: dateRange,
      practitionerIds: [selectedPractitioner.id],
   });

   const { minTime, maxTime } = getMinMaxTime(
      appointmentSchedules,
      Boolean(preferScheduleOnlyVisible),
      Boolean(preferCancelledVisitsVisible)
   );

   const {
      nextAvailableTimeSlot,
      onNextAvailableTimeSlotClick,
      noAvailableSlots,
      nextAvailableTimeSlotSearchLoading,
   } = useNextAvailableTimeSlotTools(
      appointmentSchedules,
      props.selectedDate,
      props.setSelectedDate,
      selectedPractitioner.id
   );

   return (
      <>
         <AppointmentsTimetable
            view={'timeGridWeek'}
            resource={selectedPractitioner}
            selectedDate={props.selectedDate}
            setSelectedDate={props.setSelectedDate}
            appointmentSchedules={appointmentSchedules}
            nextAvailableTimeSlot={nextAvailableTimeSlot}
            onEventChange={refetchAppointmentSchedules}
            selectionOverlapping={props.appointmentConfiguration.overlappingAppointmentsAllowed}
            preferWeekendsVisible={preferWeekendsVisible}
            interactive={!appointmentSchedulesFetching}
            preferCancelledVisitsVisible={preferCancelledVisitsVisible}
            onCancelledVisibilityChange={setCancelledVisibilityPreference}
            slotMinTime={minTime}
            slotMaxTime={maxTime}
            actions={
               <SpecialistTimetableActions selectedDate={props.selectedDate} setSelectedDate={props.setSelectedDate} />
            }
            noEventsPlaceholder={preferScheduleOnlyVisible ? <TimetablePlaceholder showTurnOffLink /> : undefined}
            noResourcesPlaceholder={<TimetablePlaceholder />}
         >
            <AppointmentsTimetable.EventSelection onUnselect={refetchAppointmentSchedules}>
               {({ selectedEvent, selectedEventElement, unselectEvent }) => (
                  <AppointmentContextDialog
                     triggerElement={selectedEventElement}
                     event={selectedEvent}
                     onEventChange={unselectEvent}
                  />
               )}
            </AppointmentsTimetable.EventSelection>

            <AppointmentsTimetable.SlotSelection onUnselect={refetchAppointmentSchedules}>
               {({ selectedSlot, unselectSlot }) => {
                  if (props.waitlistEntryId && props.bookPatientId) {
                     return (
                        <WaitlistEntrySectionLoader entryId={props.waitlistEntryId} patientId={props.bookPatientId}>
                           {({ entry }) => (
                              <AppointmentWaitlistPatientBookModal
                                 selectedSlot={selectedSlot}
                                 onClose={unselectSlot}
                                 onAppointmentCreated={unselectSlot}
                                 waitlistEntry={entry}
                              />
                           )}
                        </WaitlistEntrySectionLoader>
                     );
                  }

                  if (props.bookPatientId) {
                     return (
                        <AppointmentPatientBookModalLoader patientId={props.bookPatientId}>
                           {({ patient }) => (
                              <AppointmentPatientBookModal
                                 selectedSlot={selectedSlot}
                                 onClose={unselectSlot}
                                 onSuccess={unselectSlot}
                                 selectedPatient={patient}
                                 overlappingAppointmentsAllowed={
                                    props.appointmentConfiguration.overlappingAppointmentsAllowed
                                 }
                              />
                           )}
                        </AppointmentPatientBookModalLoader>
                     );
                  }

                  return (
                     <AppointmentBookModal
                        selectedSlot={selectedSlot}
                        onClose={unselectSlot}
                        onSuccess={unselectSlot}
                        overlappingAppointmentsAllowed={props.appointmentConfiguration.overlappingAppointmentsAllowed}
                     />
                  );
               }}
            </AppointmentsTimetable.SlotSelection>
         </AppointmentsTimetable>

         <CalendarSidePanel.Portal placement={'selectors'}>
            {protectFeature(
               'searchingAllAppointments',
               <PractitionerSelector
                  selectedPractitioner={selectedPractitioner}
                  onChange={setSelectedPractitioner}
                  practitioners={props.practitioners}
               />,
               null
            )}
         </CalendarSidePanel.Portal>

         <CalendarSidePanel.Portal placement={'miniCalendar'}>
            <MiniCalendar
               currentDate={props.selectedDate}
               onDateChange={props.setSelectedDate}
               rangeSelector={useCalendarRangeSelectorLegacy(dateRange)}
            />
         </CalendarSidePanel.Portal>

         <CalendarSidePanel.Portal placement={'underMiniCalendar'}>
            <>
               {protectFeature(
                  'searchingFirstAvailableSlotInCalendar',
                  <MiniCalendarNextAvailableTimeSlot
                     onClick={onNextAvailableTimeSlotClick}
                     noAvailableSlots={noAvailableSlots}
                     loading={nextAvailableTimeSlotSearchLoading || appointmentSchedulesFetching}
                     practitionerId={selectedPractitioner.id}
                  />,
                  null
               )}
               {props.waitlistEntryId && props.bookPatientId ? (
                  <WaitlistEntrySectionLoader entryId={props.waitlistEntryId} patientId={props.bookPatientId}>
                     {({ entry, patient }) => <WaitlistEntrySection entry={entry} patient={patient} />}
                  </WaitlistEntrySectionLoader>
               ) : null}
               {!props.waitlistEntryId && props.bookPatientId ? (
                  <CalendarPatientSectionLoader patientId={props.bookPatientId}>
                     {({ patient }) => <CalendarPatientSection patient={patient} />}
                  </CalendarPatientSectionLoader>
               ) : null}
            </>
         </CalendarSidePanel.Portal>
         {props.updateTermAppointmentId ? (
            <AppointmentTermUpdateModal appointmentId={props.updateTermAppointmentId} />
         ) : null}
         {props.restoreAppointmentId ? <AppointmentRestoreModal appointmentId={props.restoreAppointmentId} /> : null}
      </>
   );
};
